/**
 * MBA Lab — pure state/readout model for the Org Alignment Workbench. No DOM,
 * storage, or analytics dependencies - org-alignment-workbench.js owns all of
 * that and consumes buildReadout() from here.
 *
 * Applies the public Nadler-Tushman Congruence Model: 4 elements (Work,
 * People, Formal structure, Informal structure) and the 6 pairwise fits
 * between them. The diagnostic question is always fit between two elements,
 * never the quality of either alone - see alignment-elements.js and
 * Content/Leadership/the-congruence-model-for-organizational-alignment.md.
 */

import { ELEMENTS, PAIRS } from './alignment-elements.js';

export const SCHEMA_NAME = 'mbalab.org-alignment-workbench';
export const SCHEMA_VERSION = 1;

export const LIMITS = Object.freeze({
  situation: 200,
  elementNote: 1000,
  pairNote: 500,
  // Sized in bytes (UTF-8, via TextEncoder), but the character limits above
  // are enforced in UTF-16 code units (clampText uses String#slice) - same
  // gotcha planner-model.js already documents: for CJK text, 1 character is
  // 3 UTF-8 bytes but still only 1 UTF-16 unit, so a state filled to every
  // documented character limit entirely in Chinese/Japanese/Korean serializes
  // to ~22.1KB (measured directly against this module), not the ~7.2KB an
  // ASCII-only fill would produce. A 20KB budget silently failed to save
  // exactly this realistic case - audit finding, confirmed via direct
  // computation before raising this - with zero user-visible feedback,
  // since serializeForSave() returning null just means autosave() no-ops.
  maxSerializedBytes: 28 * 1024,
});

export const RATING = Object.freeze({
  GOOD_FIT: 'good-fit',
  SOME_FRICTION: 'some-friction',
  REAL_MISMATCH: 'real-mismatch',
});
const RATING_VALUES = Object.values(RATING);

const ELEMENT_KEYS = ELEMENTS.map((e) => e.key);
const PAIR_KEYS = PAIRS.map((p) => p.key);
export const TOTAL_ELEMENTS = ELEMENTS.length;
export const TOTAL_PAIRS = PAIRS.length;

// Tier-level guidance, not bespoke per pair - unlike readiness's 12 dimensions
// (each qualitatively different), these 6 pairs share the same 3-tier
// meaning, so one generic text per rating plus the pair's own prompt and the
// user's own note is enough specificity without 18 near-duplicate copy blocks.
export const RATING_TEXT = Object.freeze({
  [RATING.GOOD_FIT]: 'These two are working together - keep checking in as anything upstream changes, since a good fit today can drift.',
  [RATING.SOME_FRICTION]: "There's a real gap here worth naming directly, even if it isn't causing a visible problem yet - friction tends to compound quietly.",
  [RATING.REAL_MISMATCH]: 'This is a genuine mismatch, not a quality problem with either side alone - fixing one element without addressing the gap between them tends to just relocate the friction.',
});

function clampText(value, maxLen) {
  return typeof value === 'string' ? value.slice(0, maxLen) : '';
}

function clampSingleLineText(value, maxLen) {
  if (typeof value !== 'string') return '';
  return value.replace(/\s*[\r\n]+\s*/g, ' ').trim().slice(0, maxLen);
}

export function createInitialState() {
  const elementNotes = {};
  for (const e of ELEMENTS) elementNotes[e.key] = '';
  const pairRatings = {};
  const pairNotes = {};
  for (const p of PAIRS) {
    pairRatings[p.key] = null;
    pairNotes[p.key] = '';
  }
  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: null,
    situation: '',
    elementNotes,
    pairRatings,
    pairNotes,
  };
}

export function sanitizeState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};

  const rawElementNotes = r.elementNotes && typeof r.elementNotes === 'object' ? r.elementNotes : {};
  const elementNotes = {};
  for (const e of ELEMENTS) elementNotes[e.key] = clampText(rawElementNotes[e.key], LIMITS.elementNote);

  const rawPairRatings = r.pairRatings && typeof r.pairRatings === 'object' ? r.pairRatings : {};
  const rawPairNotes = r.pairNotes && typeof r.pairNotes === 'object' ? r.pairNotes : {};
  const pairRatings = {};
  const pairNotes = {};
  for (const p of PAIRS) {
    const v = rawPairRatings[p.key];
    pairRatings[p.key] = RATING_VALUES.includes(v) ? v : null;
    pairNotes[p.key] = clampText(rawPairNotes[p.key], LIMITS.pairNote);
  }

  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: typeof r.savedAt === 'number' ? r.savedAt : null,
    situation: clampSingleLineText(r.situation, LIMITS.situation),
    elementNotes,
    pairRatings,
    pairNotes,
  };
}

const MIGRATIONS = {
  // No prior schema version has ever shipped - see charter-model.js's
  // identical note. Proves the mechanism works before it's ever needed.
  0: (state) => ({ ...state, version: 1 }),
};

function migrate(rawState) {
  let state = rawState;
  let migrated = false;
  while (typeof state.version === 'number' && state.version < SCHEMA_VERSION && MIGRATIONS[state.version]) {
    state = MIGRATIONS[state.version](state);
    migrated = true;
  }
  return { state, migrated };
}

/** @returns {{status: 'ok'|'migrated'|'unsupported-newer'|'malformed', state: object|null, raw: string|null}} */
export function parseSavedState(raw) {
  let parsed;
  let rawString;

  if (typeof raw === 'string') {
    rawString = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null, raw: rawString };
    }
  } else {
    parsed = raw;
    try {
      rawString = raw != null ? JSON.stringify(raw) : null;
    } catch {
      rawString = null;
    }
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.schema !== SCHEMA_NAME ||
    !Number.isInteger(parsed.version) ||
    parsed.version < 0
  ) {
    return { status: 'malformed', state: null, raw: rawString };
  }
  if (parsed.version > SCHEMA_VERSION) {
    return { status: 'unsupported-newer', state: null, raw: rawString };
  }

  const { state: migratedState, migrated } = migrate(parsed);
  return { status: migrated ? 'migrated' : 'ok', state: sanitizeState(migratedState), raw: rawString };
}

export function serializeForSave(state) {
  const json = JSON.stringify(state);
  if (typeof TextEncoder !== 'undefined' && new TextEncoder().encode(json).length > LIMITS.maxSerializedBytes) {
    return null;
  }
  return json;
}

export function setSituation(state, value) {
  return { ...state, situation: clampSingleLineText(value, LIMITS.situation) };
}

export function setElementNote(state, key, value) {
  if (!ELEMENT_KEYS.includes(key)) return state;
  return { ...state, elementNotes: { ...state.elementNotes, [key]: clampText(value, LIMITS.elementNote) } };
}

export function setPairRating(state, key, rating) {
  if (!PAIR_KEYS.includes(key) || !RATING_VALUES.includes(rating)) return state;
  return { ...state, pairRatings: { ...state.pairRatings, [key]: rating } };
}

export function setPairNote(state, key, value) {
  if (!PAIR_KEYS.includes(key)) return state;
  return { ...state, pairNotes: { ...state.pairNotes, [key]: clampText(value, LIMITS.pairNote) } };
}

export function countElementsFilled(state) {
  return ELEMENTS.filter((e) => state.elementNotes[e.key].trim() !== '').length;
}

export function countPairsRated(state) {
  return PAIRS.filter((p) => state.pairRatings[p.key] !== null).length;
}

export function isComplete(state) {
  return (
    state.situation.trim() !== '' &&
    countElementsFilled(state) === TOTAL_ELEMENTS &&
    countPairsRated(state) === TOTAL_PAIRS
  );
}

/** @returns {null | {goodFit: object[], someFriction: object[], realMismatch: object[], mostMismatched: string[]}} */
export function buildReadout(state) {
  if (!isComplete(state)) return null;

  const byRating = { [RATING.GOOD_FIT]: [], [RATING.SOME_FRICTION]: [], [RATING.REAL_MISMATCH]: [] };
  for (const p of PAIRS) {
    const rating = state.pairRatings[p.key];
    byRating[rating].push({
      key: p.key,
      label: p.label,
      prompt: p.prompt,
      note: state.pairNotes[p.key],
      text: RATING_TEXT[rating],
    });
  }

  // "Where to start" insight: which element appears most often across
  // real-mismatch-rated pairs. Ties are disclosed, not silently broken -
  // matching this codebase's established tie-disclosure precedent (see
  // snapshot-model.js's TIE_NOTE_TEXT).
  const mismatchCounts = {};
  for (const e of ELEMENTS) mismatchCounts[e.key] = 0;
  for (const p of PAIRS) {
    if (state.pairRatings[p.key] !== RATING.REAL_MISMATCH) continue;
    mismatchCounts[p.aKey]++;
    mismatchCounts[p.bKey]++;
  }
  const maxCount = Math.max(...Object.values(mismatchCounts));
  const mostMismatched = maxCount > 0 ? ELEMENTS.filter((e) => mismatchCounts[e.key] === maxCount).map((e) => e.label) : [];

  const { lead: mostMismatchedLead, rest: mostMismatchedRest } = buildMostMismatchedParts(mostMismatched);

  return {
    goodFit: byRating[RATING.GOOD_FIT],
    someFriction: byRating[RATING.SOME_FRICTION],
    realMismatch: byRating[RATING.REAL_MISMATCH],
    mostMismatched,
    mostMismatchedText: mostMismatchedLead + mostMismatchedRest,
    // The element name(s) get visual emphasis in the UI (`.tool-alignment-insight
    // strong`) - exposing the split here, instead of just the flat
    // mostMismatchedText, lets the UI reconstruct that emphasis with zero
    // grammar knowledge of its own. mostMismatchedText always equals
    // mostMismatchedLead + the remainder, so a UI that only wants plain text
    // can ignore this field entirely.
    mostMismatchedLead,
  };
}

/** Proper English list joining ("A", "A and B", "A, B, and C") - a 3+ way
 * tie is real (disclosed above, not silently broken), so the sentence has
 * to read correctly for any count, not just two. */
function joinWithAnd(items) {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/** Splits the insight sentence into an emphasized lead (the element name(s)
 * that were tied, or the single element - always rendered in <strong> by
 * the UI, matching this tool's original visual pattern) and the plain-text
 * rest. `lead` is '' for the zero-mismatch case, matching that there's
 * nothing to emphasize when nothing is a real mismatch. */
function buildMostMismatchedParts(labels) {
  if (!labels.length) {
    return { lead: '', rest: 'No pair came back as a real mismatch - nothing here demands a first move.' };
  }
  if (labels.length === 1) {
    return { lead: labels[0], rest: ' shows up in the most real mismatches - a reasonable place to start.' };
  }
  const pickWord = labels.length === 2 ? 'Either' : 'Any of them';
  return {
    lead: `${joinWithAnd(labels)} are tied`,
    rest: ` - they show up in the most real mismatches. ${pickWord} is a reasonable place to start.`,
  };
}
